import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SharedToolsMenu from '../../components/ui/SharedToolsMenu';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QuickStatsGrid from './components/QuickStatsGrid';
import ClassCard from './components/ClassCard';
import GradeCard from './components/GradeCard';
import StudentOrarPanel from './components/StudentOrarPanel';
import SubjectGradesCard from './components/SubjectGradesCard';
import StudentTodayOrarWidget from './components/StudentTodayOrarWidget';
import AppointmentsCard from './components/AppointmentsCard';
import SubjectResourceDropdownCard, { type SubjectResourceItem } from './components/SubjectResourceDropdownCard';
import HomeworkSubmissionButton from './components/HomeworkSubmissionButton';
import HomeworkQuizButton from './components/HomeworkQuizButton';
import StudentNotificationsCard from './components/NotificationsCard';
import StudentSecretariatPanel from './components/StudentSecretariatPanel';
import ProfesoriPanel from '../class-management-hub/components/ProfesoriPanel';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import type { Class, QuickStat } from './types';
import type { NotificationItem } from '../../lib/teacherApi';

type GradebookConfigLite = {
  id: number;
  classroom_id: number;
  school_year: string;
  structure: 'semestru' | 'trimestru' | 'module' | string;
  education_level: 'primar' | 'gimnazial' | 'liceal' | 'universitar' | string;
  locked: boolean;
};

type ClassroomMembershipView = {
  id: number;
  name: string;
  profesor: string;
  is_admin: boolean;
};

type ClassroomStats = {
  classroom_id: number;
  classroom_name: string;
  students_count: number;
  school?: {
    id: number | null;
    name: string | null;
    rating_percent: number | null;
    rating_percent_prev?: number | null;
    rating_delta?: number | null;
    rating_trend?: 'up' | 'down' | 'flat' | null;
    rating_trend_window_days?: number | null;
  };
  coordinator: { id: number | null; full_name: string | null; email: string | null };
  avg_grade: number | null;
  rating_percent: number | null;
  rating_percent_prev?: number | null;
  rating_delta?: number | null;
  rating_trend?: 'up' | 'down' | 'flat' | null;
  rating_trend_window_days?: number | null;
  top_rank: number | null;
  top_message: string;
};

type SubjectLite = { id: number; name: string; professor_id?: number };
type TeacherLite = { id: number; full_name: string };

type GradeOut = {
  id: number;
  student_id: number;
  subject_id: number | null;
  teacher_id?: number | null;
  teacher_name?: string | null;
  classroom_id?: number | null;
  gradebook_config_id?: number | null;
  period_index?: number | null;
  comment?: string | null;
  category_name?: string | null;
  date?: string | null;
  is_thesis?: boolean;
  is_homework?: boolean;
  is_lab?: boolean;
  is_seminar?: boolean;
  is_final?: boolean;
  value: number | null;
  qualifier: string | null;
  is_conduct: boolean;
  created_at: string;
};

type AbsenceOut = {
  id: number;
  student_id: number;
  subject_id: number | null;
  justified: string | null;
  date?: string | null;
  created_at: string;
  classroom_id?: number | null;
  gradebook_config_id?: number | null;
  period_index?: number | null;
  justification_reason?: string | null;
};

type HomeworkFile = { id: number; file_url: string; original_name: string };
type HomeworkItem = {
  id: number;
  subject_id: number | null;
  subject: string | null;
  title: string;
  description: string | null;
  created_at: string;
  has_quiz?: boolean;
  quiz_time_limit_seconds?: number | null;
  quiz_submission?: {
    submitted_at: string | null;
    score: number | null;
    total: number | null;
    duration_seconds: number | null;
  } | null;
  files: HomeworkFile[];
};

type CourseItem = {
  id: number;
  subject_id: number | null;
  subject: string | null;
  title: string;
  comment: string | null;
  created_at: string;
  files: HomeworkFile[];
};

type SubjectGroup<T> = {
  key: string;
  subjectName: string;
  items: T[];
};

function subjectGroupKey(subjectId: number | null, subjectName: string | null): { key: string; name: string } {
  const name = (subjectName || '').toString().trim() || 'Fără materie';
  if (subjectId != null) return { key: `sid:${Number(subjectId)}`, name };
  return { key: `sname:${name.toLowerCase()}`, name };
}

function periodLabels(structure: string): string[] {
  const s = (structure || '').toString().toLowerCase();
  if (s === 'semestru') return ['Semestrul 1', 'Semestrul 2'];
  if (s === 'trimestru') return ['Trimestrul 1', 'Trimestrul 2', 'Trimestrul 3'];
  return ['Modul 1', 'Modul 2', 'Modul 3', 'Modul 4', 'Modul 5'];
}

function gradeToNumber(value: number | null, qualifier: string | null, educationLevel: string): number | null {
  if (typeof value === 'number') return value;
  const lvl = (educationLevel || '').toString().trim().toLowerCase();
  if (lvl !== 'primar') return null;
  const q = (qualifier || '').toString().trim().toUpperCase();
  if (!q) return null;
  if (q === 'FB') return 10;
  if (q === 'B') return 8;
  if (q === 'S') return 6;
  if (q === 'I') return 4;
  return null;
}

function avgOf(nums: number[]): number | null {
  if (!nums.length) return null;
  return Math.round((nums.reduce((s, v) => s + v, 0) / nums.length) * 100) / 100;
}

function clamp(min: number, v: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function mapEducationLevelToDashboardLevel(
  educationLevel: string
): 'primary' | 'middle' | 'high' | 'university' {
  const lvl = (educationLevel || '').toString().trim().toLowerCase();
  if (lvl === 'primar') return 'primary';
  if (lvl === 'gimnazial') return 'middle';
  if (lvl === 'liceal') return 'high';
  if (lvl === 'universitar') return 'university';
  return 'high';
}

function gradeKind(g: GradeOut): 'regular' | 'thesis' | 'lab' | 'seminar' | 'homework' | 'final' | 'conduct' {
  if (g.is_conduct) return 'conduct';
  if (g.is_thesis) return 'thesis';
  if (g.is_lab) return 'lab';
  if (g.is_seminar) return 'seminar';
  if (g.is_homework) return 'homework';
  if (g.is_final) return 'final';
  return 'regular';
}

const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { me, uiRole, isLoading: authLoading, refresh, signOut } = useAuth();

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const [activeTab, setActiveTab] = useState<'prezentare' | 'teme' | 'cursuri' | 'note' | 'orar' | 'profesori' | 'secretariat'>('prezentare');
  const [pendingDeepLink, setPendingDeepLink] = useState<{ tab?: string; classId?: number } | null>(() => {
    const rawTab = (searchParams.get('tab') || '').trim();
    const rawClassId = (searchParams.get('classId') || '').trim();
    const classId = rawClassId ? Number(rawClassId) : NaN;
    return {
      tab: rawTab || undefined,
      classId: Number.isFinite(classId) ? classId : undefined,
    };
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);

  const [refreshNonce, setRefreshNonce] = useState(0);

  const [classes, setClasses] = useState<ClassroomMembershipView[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [classStatsById, setClassStatsById] = useState<Record<number, ClassroomStats | undefined>>({});

  const [configs, setConfigs] = useState<GradebookConfigLite[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const currentConfig = useMemo(
    () => configs.find((c) => Number(c.id) === Number(selectedConfigId)) || null,
    [configs, selectedConfigId]
  );

  const [periodIndex, setPeriodIndex] = useState(0);

  const [subjects, setSubjects] = useState<SubjectLite[]>([]);
  const [teachers, setTeachers] = useState<TeacherLite[]>([]);
  const teacherNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of teachers) map.set(Number(t.id), (t.full_name || '').toString());
    return map;
  }, [teachers]);
  const subjectNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of subjects) map.set(Number(s.id), (s.name || '').toString());
    return map;
  }, [subjects]);
  const subjectProfessorIdById = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of subjects) {
      if (s.professor_id != null) map.set(Number(s.id), Number(s.professor_id));
    }
    return map;
  }, [subjects]);

  const [grades, setGrades] = useState<GradeOut[]>([]);
  const [absences, setAbsences] = useState<AbsenceOut[]>([]);
  const [homeworks, setHomeworks] = useState<HomeworkItem[]>([]);
  const [courses, setCourses] = useState<CourseItem[]>([]);

  const apiBase = useMemo(() => (api.defaults.baseURL || 'http://localhost:8000').toString().replace(/\/$/, ''), []);

  const refreshUnreadNotifications = async () => {
    try {
      const resp = await api.get<{ unread: number }>('/api/notificari/notifications/unread-count');
      const unread = Number((resp.data as any)?.unread ?? 0);
      setUnreadNotifications(Number.isFinite(unread) ? unread : 0);
    } catch {
      // ignore
    }
  };

  const loadNotifications = async () => {
    try {
      const resp = await api.get('/api/notificari/notifications', { params: { page: 1, page_size: 10 } });
      const list = Array.isArray((resp.data as any)?.items) ? ((resp.data as any).items as any[]) : [];
      setNotifications(list);
    } catch {
      setNotifications([]);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('/api/notificari/notifications/mark-as-read');
    } catch {
      // ignore
    }
    await refreshUnreadNotifications();
    await loadNotifications();
  };

  const openNotification = async (n: NotificationItem) => {
    try {
      if (!n.is_read) await api.post(`/api/notificari/notifications/${n.id}/read`);
    } catch {
      // ignore
    }
    await refreshUnreadNotifications();
    if (n.link) navigate(n.link);
  };

  const uiClasses: Class[] = useMemo(() => {
    const palette = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#DC2626', '#0EA5E9'];
    return classes.map((c, idx) => ({
      id: String(c.id),
      name: c.name,
      teacher: c.profesor || null,
      subject: null,
      color: palette[idx % palette.length],
      isOnline: false,
      classRatingPercent: classStatsById[c.id]?.rating_percent ?? null,
      classRatingPercentPrev: classStatsById[c.id]?.rating_percent_prev ?? null,
      classRatingDelta: classStatsById[c.id]?.rating_delta ?? null,
      classRatingTrend: classStatsById[c.id]?.rating_trend ?? null,
      classRatingTrendWindowDays: classStatsById[c.id]?.rating_trend_window_days ?? null,
      studentsCount: classStatsById[c.id]?.students_count ?? null,
      coordinatorName: classStatsById[c.id]?.coordinator?.full_name ?? null,
      coordinatorEmail: classStatsById[c.id]?.coordinator?.email ?? null,
      topRank: classStatsById[c.id]?.top_rank ?? null,
      topMessage: classStatsById[c.id]?.top_message ?? null,
    }));
  }, [classStatsById, classes]);

  useEffect(() => {
    if (authLoading) return;
    if (!me) return;
    if (uiRole !== 'student') return;
    if (!classes.length) {
      setClassStatsById({});
      return;
    }

    let cancelled = false;
    const loadStats = async () => {
      try {
        const results = await Promise.all(
          classes.map(async (c) => {
            try {
              const resp = await api.get(`/api/scoli/classrooms/${c.id}/stats`);
              return [c.id, resp.data as ClassroomStats] as const;
            } catch {
              return [c.id, undefined] as const;
            }
          })
        );
        if (cancelled) return;
        const next: Record<number, ClassroomStats | undefined> = {};
        for (const [id, st] of results) next[id] = st;
        setClassStatsById(next);
      } catch {
        if (!cancelled) setClassStatsById({});
      }
    };

    void loadStats();
    return () => {
      cancelled = true;
    };
  }, [authLoading, classes, me, uiRole]);

  const quickStats: QuickStat[] = useMemo(() => {
    const educationLevel = (currentConfig?.education_level || '').toString();
    const nums = grades
      .filter((g) => !g.is_conduct)
      .map((g) => gradeToNumber(g.value, g.qualifier, educationLevel))
      .filter((v): v is number => typeof v === 'number');
    const avg = avgOf(nums);

    const absTotal = absences.length;
    const absMot = absences.filter((a) => (a.justified || '').toString().toLowerCase() === 'da').length;

    return [
      { label: 'Clase', value: classes.length, icon: 'BookOpen', color: '#2563EB' },
      { label: 'Teme', value: homeworks.length, icon: 'FileText', color: '#D97706' },
      { label: 'Medie', value: avg != null ? String(avg) : '—', icon: 'Award', color: '#059669' },
      { label: 'Absente', value: absTotal ? `${absTotal} (${absMot} motivate)` : '0', icon: 'Calendar', color: '#DC2626' },
    ];
  }, [absences, classes.length, currentConfig?.education_level, grades, homeworks.length]);

  const studentRating = useMemo(() => {
    const educationLevel = (currentConfig?.education_level || '').toString();

    const numericGrades = grades
      .filter((g) => !g.is_conduct)
      .map((g) => ({
        g,
        v: gradeToNumber(g.value, g.qualifier, educationLevel),
      }))
      .filter((x): x is { g: GradeOut; v: number } => typeof x.v === 'number');

    const avg = avgOf(numericGrades.map((x) => x.v));

    const absTotal = absences.length;
    const base = avg != null ? (avg / 10) * 100 : absTotal > 0 ? 55 : null;
    const penalty = clamp(0, absTotal * 5, 35);
    const ratingPercent = base != null ? Math.round(clamp(0, base - penalty, 100)) : null;

    const withDate = numericGrades
      .map((x) => {
        const raw = (x.g.date || x.g.created_at || '').toString();
        if (!raw) return null;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        return { d, v: x.v };
      })
      .filter((x): x is { d: Date; v: number } => !!x)
      .sort((a, b) => a.d.getTime() - b.d.getTime());

    let trend: 'up' | 'down' | 'flat' = 'flat';
    let avgDelta: number | null = null;
    if (withDate.length >= 2) {
      const splitIndex = Math.floor(withDate.length / 2);
      const first = withDate.slice(0, splitIndex).map((x) => x.v);
      const second = withDate.slice(splitIndex).map((x) => x.v);
      const a1 = avgOf(first);
      const a2 = avgOf(second);
      if (a1 != null && a2 != null) {
        avgDelta = Math.round((a2 - a1) * 100) / 100;
        if (avgDelta > 0.3) trend = 'up';
        else if (avgDelta < -0.3) trend = 'down';
      }
    }

    return { ratingPercent, trend, avg, avgDelta, absTotal };
  }, [absences.length, currentConfig?.education_level, grades]);

  const groupedBySubject = useMemo(() => {
    const educationLevel = mapEducationLevelToDashboardLevel(currentConfig?.education_level || '');
    const groups = new Map<string, { key: string; subjectId: number | null; subjectName: string; teacherName: string | null; grades: GradeOut[]; absences: AbsenceOut[] }>();

    const getKey = (subjectId: number | null) => (subjectId == null ? 'conduct' : `s:${subjectId}`);
    const ensure = (subjectId: number | null) => {
      const key = getKey(subjectId);
      const existing = groups.get(key);
      if (existing) return existing;
      const subjectName =
        subjectId == null
          ? 'Purtare'
          : subjectNameById.get(Number(subjectId)) || `Materia #${subjectId}`;
      const profId = subjectId == null ? null : subjectProfessorIdById.get(Number(subjectId)) ?? null;
      const teacherName =
        (profId != null ? (teacherNameById.get(Number(profId)) || '').toString().trim() : '') || null;
      const next = { key, subjectId, subjectName, teacherName, grades: [] as GradeOut[], absences: [] as AbsenceOut[] };
      groups.set(key, next);
      return next;
    };

    for (const g of grades) {
      const group = ensure(g.subject_id ?? null);
      group.grades.push(g);
      if (!group.teacherName) {
        const t =
          (g.teacher_name || '').toString().trim() ||
          (g.teacher_id != null ? (teacherNameById.get(Number(g.teacher_id)) || '').toString().trim() : '');
        group.teacherName = t || group.teacherName;
      }
    }

    for (const a of absences) {
      const group = ensure(a.subject_id ?? null);
      group.absences.push(a);
    }

    const list = Array.from(groups.values());
    list.sort((a, b) => {
      if (a.subjectId == null) return 1;
      if (b.subjectId == null) return -1;
      return a.subjectName.localeCompare(b.subjectName);
    });

    return { educationLevel, list };
  }, [absences, currentConfig?.education_level, grades, subjectNameById, subjectProfessorIdById, teacherNameById]);

  const groupedHomeworks = useMemo((): SubjectGroup<HomeworkItem>[] => {
    const groups = new Map<string, SubjectGroup<HomeworkItem>>();
    for (const hw of homeworks) {
      const { key, name } = subjectGroupKey(hw.subject_id ?? null, hw.subject ?? null);
      const g = groups.get(key) || { key, subjectName: name, items: [] };
      g.items.push(hw);
      groups.set(key, g);
    }
    const list = Array.from(groups.values());
    list.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
    return list;
  }, [homeworks]);

  const groupedCourses = useMemo((): SubjectGroup<CourseItem>[] => {
    const groups = new Map<string, SubjectGroup<CourseItem>>();
    for (const c of courses) {
      const { key, name } = subjectGroupKey(c.subject_id ?? null, c.subject ?? null);
      const g = groups.get(key) || { key, subjectName: name, items: [] };
      g.items.push(c);
      groups.set(key, g);
    }
    const list = Array.from(groups.values());
    list.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
    return list;
  }, [courses]);

  useEffect(() => {
    if (authLoading) return;
    if (!me) return;
    if (uiRole !== 'student') return;

    let cancelled = false;
    const loadClasses = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get('/api/scoli/users/my-classes');
        const list = Array.isArray(resp.data) ? (resp.data as ClassroomMembershipView[]) : [];
        if (!cancelled) {
          setClasses(list);
          setSelectedClassId((prev) => {
            if (prev && list.some((c) => Number(c.id) === Number(prev))) return prev;
            return list[0]?.id ?? null;
          });
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Nu s-au putut incarca clasele.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadClasses();
    return () => {
      cancelled = true;
    };
  }, [authLoading, me, refreshNonce, uiRole]);

  useEffect(() => {
    if (!pendingDeepLink) return;
    if (!classes.length) return;

    const allowedTabs = new Set(['prezentare', 'teme', 'cursuri', 'note', 'orar', 'profesori', 'secretariat']);
    const rawTab = (pendingDeepLink.tab || '').trim();
    const nextTab = allowedTabs.has(rawTab) ? (rawTab as any) : null;

    const desiredClassId = pendingDeepLink.classId;
    if (desiredClassId && classes.some((c) => Number(c.id) === Number(desiredClassId))) {
      setSelectedClassId(Number(desiredClassId));
    }
    if (nextTab) setActiveTab(nextTab);
    setPendingDeepLink(null);
  }, [classes, pendingDeepLink]);

  useEffect(() => {
    if (authLoading) return;
    if (!me) return;
    if (uiRole !== 'student') return;
    void refreshUnreadNotifications();
    void loadNotifications();
    const t = window.setInterval(() => void refreshUnreadNotifications(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me, uiRole]);

  useEffect(() => {
    if (!selectedClassId) {
      setConfigs([]);
      setSelectedConfigId(null);
      return;
    }
    let cancelled = false;
    const loadConfigs = async () => {
      try {
        const resp = await api.get(`/api/catalog/config/all/${selectedClassId}`);
        const list = Array.isArray(resp.data) ? (resp.data as GradebookConfigLite[]) : [];
        const ordered = list.slice().sort((a, b) => Number(b.id) - Number(a.id));
        if (cancelled) return;
        setConfigs(ordered);
        setSelectedConfigId((prev) => {
          if (prev && ordered.some((c) => Number(c.id) === Number(prev))) return prev;
          return ordered[0]?.id ?? null;
        });
      } catch {
        if (!cancelled) {
          setConfigs([]);
          setSelectedConfigId(null);
        }
      }
    };
    void loadConfigs();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce, selectedClassId]);

  useEffect(() => {
    setPeriodIndex(0);
  }, [selectedConfigId]);

  useEffect(() => {
    if (!me?.id) return;
    if (!selectedClassId || !selectedConfigId) {
      setSubjects([]);
      setGrades([]);
      setAbsences([]);
      setHomeworks([]);
      setCourses([]);
      return;
    }

    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [subjectsResp, gradesResp, absResp, temeResp, cursResp, teachersResp] = await Promise.all([
          api.get(`/api/materii/subjects/by-class/${selectedClassId}`),
          // Fetch all grades/absences then filter client-side (keeps legacy rows with null cfg/period).
          api.get(`/api/grades/student/${me.id}`),
          api.get(`/api/absente/student/${me.id}`),
          api.get(`/api/teme/lista/${selectedClassId}`, { params: { gradebook_config_id: selectedConfigId, period_index: periodIndex } }),
          api.get(`/api/cursuri/lista/${selectedClassId}`, { params: { gradebook_config_id: selectedConfigId, period_index: periodIndex } }),
          api.get(`/api/scoli/classrooms/${selectedClassId}/teachers`),
        ]);

        if (cancelled) return;
        setSubjects(Array.isArray(subjectsResp.data) ? (subjectsResp.data as SubjectLite[]) : []);
        setTeachers(Array.isArray(teachersResp.data) ? (teachersResp.data as TeacherLite[]) : []);

        const allGrades = Array.isArray(gradesResp.data) ? (gradesResp.data as GradeOut[]) : [];
        const allAbs = Array.isArray(absResp.data) ? (absResp.data as AbsenceOut[]) : [];

        const filteredGrades = allGrades.filter((g) => {
          if (g.classroom_id != null && Number(g.classroom_id) !== Number(selectedClassId)) return false;
          const cfgOk = g.gradebook_config_id == null || Number(g.gradebook_config_id) === Number(selectedConfigId);
          const periodOk = g.period_index == null || Number(g.period_index) === Number(periodIndex);
          return cfgOk && periodOk;
        });

        const filteredAbs = allAbs.filter((a) => {
          if (a.classroom_id != null && Number(a.classroom_id) !== Number(selectedClassId)) return false;
          const cfgOk = a.gradebook_config_id == null || Number(a.gradebook_config_id) === Number(selectedConfigId);
          const periodOk = a.period_index == null || Number(a.period_index) === Number(periodIndex);
          return cfgOk && periodOk;
        });

        setGrades(filteredGrades);
        setAbsences(filteredAbs);
        setHomeworks(Array.isArray(temeResp.data) ? (temeResp.data as HomeworkItem[]) : []);
        setCourses(Array.isArray(cursResp.data) ? (cursResp.data as CourseItem[]) : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Nu s-au putut incarca datele.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [me?.id, periodIndex, refreshNonce, selectedClassId, selectedConfigId]);

  const tabs = [
    { id: 'prezentare', label: 'Prezentare', icon: 'LayoutDashboard' },
    { id: 'orar', label: 'Orar', icon: 'Calendar' },
    { id: 'teme', label: 'Teme', icon: 'FileText' },
    { id: 'cursuri', label: 'Cursuri', icon: 'BookOpen' },
    { id: 'note', label: 'Note', icon: 'Award' },
    { id: 'profesori', label: 'Profesori', icon: 'Users' },
    { id: 'secretariat', label: 'Secretariat', icon: 'FolderOpen' },
  ] as const;

  const selectedClass = useMemo(
    () => classes.find((c) => Number(c.id) === Number(selectedClassId)) || null,
    [classes, selectedClassId]
  );

  const selectedClassStats = useMemo(
    () => (selectedClassId ? classStatsById[Number(selectedClassId)] : undefined),
    [classStatsById, selectedClassId]
  );

  useEffect(() => {
    if (uiRole === 'student' && me?.must_change_password) setShowPwdModal(true);
  }, [me?.must_change_password, uiRole]);

  const validatePassword = (pwd: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwd);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPwdError('Parolele nu coincid.');
      return;
    }
    if (!validatePassword(newPassword)) {
      setPwdError('Parola trebuie sa contina litere mari/mici, cifre si simboluri.');
      return;
    }
    setPwdLoading(true);
    setPwdError('');
    try {
      await api.put('/api/users/me/password/initial', {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      await refresh();
      alert('Parola a fost schimbata. Vei fi delogat.');
      signOut();
      navigate('/authentication-portal');
    } catch (err: any) {
      setPwdError(err?.response?.data?.detail || 'Eroare la schimbarea parolei.');
    } finally {
      setPwdLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Se incarca…</div>
      </div>
    );
  }

  if (!me || uiRole !== 'student') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Nu esti autentificat ca elev.</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Panou elev - Scola</title>
        <meta name="description" content="Panou elev: clase, teme, cursuri, note si absente." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header user={{ role: 'student', name: me.full_name || 'Elev', avatar: me.avatar_url || undefined }} onNavigate={(p) => navigate(p)} onSignOut={() => signOut()} />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {error && <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-1">Bine ai revenit, {(me.full_name || 'Elev').split(' ')[0]}!</h1>
                {selectedClass ? (
                  <div className="space-y-0.5">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-sm">
                      <span className="min-w-0">
                        Scoala:{' '}
                        <span className="text-foreground">{selectedClassStats?.school?.name || '—'}</span>
                      </span>
                      {typeof selectedClassStats?.school?.rating_percent === 'number' && (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-foreground font-medium">{selectedClassStats.school.rating_percent}%</span>
                          {selectedClassStats.school.rating_trend && typeof selectedClassStats.school.rating_delta === 'number' && (
                            <Icon
                              name={
                                selectedClassStats.school.rating_trend === 'up'
                                  ? 'TrendingUp'
                                  : selectedClassStats.school.rating_trend === 'down'
                                    ? 'TrendingDown'
                                    : 'Minus'
                              }
                              size={16}
                              className={
                                selectedClassStats.school.rating_trend === 'up'
                                  ? 'text-success'
                                  : selectedClassStats.school.rating_trend === 'down'
                                    ? 'text-error'
                                    : 'text-muted-foreground'
                              }
                              title={
                                selectedClassStats.school.rating_trend === 'up'
                                  ? `In urcare (+${selectedClassStats.school.rating_delta} pp, ultimele ${selectedClassStats.school.rating_trend_window_days ?? 30} zile)`
                                  : selectedClassStats.school.rating_trend === 'down'
                                    ? `In coborare (${selectedClassStats.school.rating_delta} pp, ultimele ${selectedClassStats.school.rating_trend_window_days ?? 30} zile)`
                                    : `Stagnare (0 pp, ultimele ${selectedClassStats.school.rating_trend_window_days ?? 30} zile)`
                              }
                            />
                          )}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">Clasa: {selectedClass.name}</p>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <span>Rating elev:</span>
                      <span className="text-foreground font-medium">
                        {typeof studentRating.ratingPercent === 'number' ? `${studentRating.ratingPercent}%` : '—'}
                      </span>
                      <Icon
                        name={studentRating.trend === 'up' ? 'TrendingUp' : studentRating.trend === 'down' ? 'TrendingDown' : 'Minus'}
                        size={16}
                        className={
                          studentRating.trend === 'up'
                            ? 'text-success'
                            : studentRating.trend === 'down'
                              ? 'text-error'
                              : 'text-muted-foreground'
                        }
                        title={
                          studentRating.avg == null
                            ? studentRating.absTotal > 0
                              ? 'Nu există note încă; rating estimat pe baza absențelor.'
                              : 'Nu există suficiente date pentru rating.'
                            : studentRating.trend === 'up'
                              ? `În urcare (Δ medie: +${studentRating.avgDelta ?? 0})`
                              : studentRating.trend === 'down'
                                ? `În coborâre (Δ medie: ${studentRating.avgDelta ?? 0})`
                                : `Stagnare (Δ medie: ${studentRating.avgDelta ?? 0})`
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Nu esti inscris in nicio clasa.</p>
                )}
              </div>
              <div className="w-full sm:w-auto grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="AlertTriangle"
                  className="col-span-2 min-[420px]:col-span-1 sm:col-auto"
                  fullWidth
                  onClick={() =>
                    navigate('/contact?category=abuse&subject=Raportare%20abuz', {
                      state: { backgroundLocation: location },
                    })
                  }
                >
                  Raportează abuz
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  className="col-span-2 min-[420px]:col-span-1 sm:col-auto"
                  fullWidth
                  onClick={() => navigate('/documents')}
                >
                  Documente
                </Button>
                <div className="col-span-1 sm:col-auto flex items-center">
                  <SharedToolsMenu user={{ role: 'student' }} onNavigate={(p) => navigate(p)} />
                </div>
                <div className="col-span-1 sm:col-auto flex items-center justify-end">
                  <button
                    onClick={() => {
                      setUnreadNotifications(0);
                      navigate('/notifications');
                    }}
                    className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
                    aria-label={`Notificări${unreadNotifications > 0 ? ` (${unreadNotifications} necitite)` : ''}`}
                  >
                    <Icon name="Bell" size={20} />
                    {unreadNotifications > 0 && (
                      <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-error rounded-full">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block text-sm font-medium text-foreground">
                  Clasa
                  <select
                    value={selectedClassId ?? ''}
                    onChange={(e) => setSelectedClassId(e.target.value ? Number(e.target.value) : null)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={loading}
                  >
                    <option value="">-- selecteaza --</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm font-medium text-foreground">
                  Catalog
                  <select
                    value={selectedConfigId ?? ''}
                    onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : null)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={loading || !configs.length}
                  >
                    <option value="">-- selecteaza --</option>
                    {configs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.school_year} • {c.structure}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex items-end justify-end">
                  <Button
                    variant="outline"
                    iconName="RefreshCcw"
                    iconPosition="left"
                    onClick={() => setRefreshNonce((n) => n + 1)}
                    disabled={loading}
                  >
                    Reincarca
                  </Button>
                </div>
              </div>

              {currentConfig && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {periodLabels(currentConfig.structure).map((label, idx) => (
                    <button
                      key={label}
                      onClick={() => setPeriodIndex(idx)}
                      className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                        periodIndex === idx ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                      }`}
                      disabled={loading}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <QuickStatsGrid stats={quickStats} />

            <div className="mt-8 border-b border-border">
              <nav
                className="flex gap-1 overflow-x-auto overscroll-x-contain -mx-4 px-4 sm:mx-0 sm:px-0"
                aria-label="Sectiuni panou elev"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex shrink-0 items-center gap-2 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-8">
              {activeTab === 'prezentare' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-foreground">Clasele mele</h2>
                      </div>
                      {uiClasses.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu esti inscris in nicio clasa.</div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {uiClasses.map((classData) => (
                            <ClassCard key={classData.id} classData={classData} />
                          ))}
                        </div>
                      )}
                    </div>

                    <StudentNotificationsCard
                      notifications={notifications}
                      onReload={async () => {
                        await loadNotifications();
                        await refreshUnreadNotifications();
                      }}
                      onMarkAllRead={markAllNotificationsRead}
                      onOpenAll={() => navigate('/notifications')}
                      onOpen={openNotification}
                    />
                  </div>

                  <div className="space-y-6">
                    <StudentTodayOrarWidget
                      classId={selectedClassId}
                      gradebookConfigId={selectedConfigId}
                      periodIndex={periodIndex}
                      onOpenStudentSchedule={() => setActiveTab('orar')}
                    />

                    <AppointmentsCard />

                    <div className="bg-card border border-border rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Note recente</h3>
                      {grades.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu exista note in perioada selectata.</div>
                      ) : (
                        <div className="space-y-3">
                          {grades
                            .filter((g) => !g.is_conduct)
                            .slice()
                            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                            .slice(0, 4)
                            .map((g) => (
                              <GradeCard
                                key={g.id}
                                grade={{
                                  id: String(g.id),
                                  subject: g.subject_id ? subjectNameById.get(Number(g.subject_id)) || `Materia #${g.subject_id}` : g.is_conduct ? 'Purtare' : '—',
                                  grade: g.value != null ? g.value : g.qualifier || '—',
                                  date: g.created_at,
                                  description: (g.comment || '').toString(),
                                  teacher:
                                    (g.teacher_name || '').toString().trim() ||
                                    (g.teacher_id != null ? (teacherNameById.get(Number(g.teacher_id)) || '').toString().trim() : '') ||
                                    '—',
                                  type: gradeKind(g),
                                }}
                                educationLevel={mapEducationLevelToDashboardLevel(currentConfig?.education_level || '')}
                              />
                            ))}
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        onClick={() => setActiveTab('note')}
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="mt-4"
                      >
                        Vezi toate notele
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'teme' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Teme</h2>
                    <div className="text-xs text-muted-foreground">{homeworks.length} teme</div>
                  </div>
                  {homeworks.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nu exista teme in perioada selectata.</div>
                  ) : (
                    <div className="space-y-3">
                      {groupedHomeworks.map((g) => {
                        const items: SubjectResourceItem[] = g.items.map((t) => ({
                          id: t.id,
                          title: t.title,
                          created_at: t.created_at,
                          text: t.description,
                          files: t.files,
                          has_quiz: Boolean((t as any).has_quiz),
                          quiz_submission: (t as any).quiz_submission ?? null,
                        }));
                        return (
                          <SubjectResourceDropdownCard
                            key={g.key}
                            subjectName={g.subjectName}
                            countLabel={`${g.items.length} teme`}
                            apiBase={apiBase}
                            items={items}
                            downloadKind="teme"
                            renderSelectedActions={(selected) => (
                              <div className="flex flex-col items-end gap-2">
                                {(selected as any).has_quiz ? (
                                  (selected as any).quiz_submission?.submitted_at ? (
                                    <div className="text-right">
                                      <div className="text-xs text-muted-foreground">Quiz rezolvat</div>
                                      <div className="text-sm font-semibold text-foreground">
                                        {(selected as any).quiz_submission?.score ?? '—'} / {(selected as any).quiz_submission?.total ?? '—'}
                                      </div>
                                      <HomeworkQuizButton homeworkId={Number(selected.id)} label="Vezi rezultat" />
                                    </div>
                                  ) : (
                                    <HomeworkQuizButton homeworkId={Number(selected.id)} />
                                  )
                                ) : null}
                                <HomeworkSubmissionButton homeworkId={Number(selected.id)} />
                              </div>
                            )}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'orar' && (
                <StudentOrarPanel
                  classId={Number(selectedClassId || 0)}
                  gradebookConfigId={selectedConfigId}
                  periodIndex={periodIndex}
                  onOpenClassSchedule={() => {
                    if (selectedClassId) navigate(`/class-management-hub?classId=${selectedClassId}&tab=orar`);
                  }}
                />
              )}

              {activeTab === 'cursuri' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Cursuri</h2>
                    <div className="text-xs text-muted-foreground">{courses.length} cursuri</div>
                  </div>
                  {courses.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nu exista cursuri in perioada selectata.</div>
                  ) : (
                    <div className="space-y-3">
                      {groupedCourses.map((g) => {
                        const items: SubjectResourceItem[] = g.items.map((c) => ({
                          id: c.id,
                          title: c.title,
                          created_at: c.created_at,
                          text: c.comment,
                          files: c.files,
                        }));
                        return (
                          <SubjectResourceDropdownCard
                            key={g.key}
                            subjectName={g.subjectName}
                            countLabel={`${g.items.length} cursuri`}
                            apiBase={apiBase}
                            items={items}
                            downloadKind="cursuri"
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'note' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Note</h2>
                    <div className="text-xs text-muted-foreground">{grades.filter((g) => !g.is_conduct).length} note</div>
                  </div>
                  {grades.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nu exista note in perioada selectata.</div>
                  ) : (
                    <div className="space-y-4">
                      {groupedBySubject.list.map((group) => (
                        <SubjectGradesCard
                          key={group.key}
                          subjectName={group.subjectName}
                          teacherName={group.teacherName}
                          educationLevel={groupedBySubject.educationLevel}
                          grades={group.grades.map((g) => ({
                            id: g.id,
                            value: g.value,
                            qualifier: g.qualifier,
                            comment: g.comment ?? null,
                            date: g.date ?? null,
                            created_at: g.created_at,
                            is_thesis: !!g.is_thesis,
                            is_homework: !!g.is_homework,
                            is_lab: !!g.is_lab,
                            is_seminar: !!g.is_seminar,
                            is_final: !!g.is_final,
                            is_conduct: !!g.is_conduct,
                            category_name: g.category_name ?? null,
                            teacher_name: g.teacher_name ?? null,
                          }))}
                          absences={group.absences.map((a) => ({
                            id: a.id,
                            justified: a.justified,
                            date: a.date ?? null,
                            created_at: a.created_at,
                            justification_reason: a.justification_reason ?? null,
                          }))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'secretariat' && <StudentSecretariatPanel />}

              {activeTab === 'profesori' && (
                selectedClassId ? (
                  <ProfesoriPanel classroomId={Number(selectedClassId)} />
                ) : (
                  <div className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground">
                    Selecteaza o clasa pentru a vedea lista de profesori.
                  </div>
                )
              )}
            </div>
          </div>
        </main>

        {showPwdModal && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Schimba parola initiala</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Pentru securitate, te rugam sa iti setezi o parola noua.
              </p>

              {pwdError && <div className="mb-3 text-sm text-error">{pwdError}</div>}

              <div className="space-y-3">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Parola noua"
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Confirma parola"
                  autoComplete="new-password"
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    signOut();
                    navigate('/authentication-portal');
                  }}
                >
                  Delogare
                </Button>
                <Button variant="default" onClick={handleChangePassword} loading={pwdLoading}>
                  Salveaza
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentDashboard;
